import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Stack, Typography, Divider, Link, Box, alpha } from '@mui/material'
import { Check, ArrowUpRight, MoonIcon, SunIcon } from 'lucide-react'
import { Card, ToggleButton, Button } from '@repo/ui'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

/** Which Studio surface the logo controls apply to (drives copy + notes). */
type LogoSurface = 'email' | 'certificate'
type LogoVariant = 'mark' | 'wordmark'
/**
 * Which of the four logo assets the tiles are currently showing.
 *
 * NOT a persisted choice, and deliberately not one. A brand does not pick whether its
 * logo renders on light or dark — the READER's mail client does, via
 * `prefers-color-scheme`, and the email ships both assets with
 * `.v-logo-light`/`.v-logo-dark` swapping them at render time
 * (`packages/emails/src/components/base-email.tsx`). So this toggle changes what you are
 * LOOKING at, never what is saved. The saved choice is mark-vs-wordmark, which applies to
 * both appearances at once.
 */
type LogoAppearance = 'light' | 'dark'

interface LogoUploadCardProps {
  /** Square mark on a light surface — `company.logo`. The only required variant. */
  markLogo?: string
  /** Square mark on a dark surface — `company.logo_dark`. */
  markLogoDark?: string | null
  /** Wide wordmark on a light surface — `company.logo_wide`. Null if never uploaded. */
  wordmarkLogo?: string | null
  /** Wide wordmark on a dark surface — `company.logo_wide_dark`. */
  wordmarkLogoDark?: string | null
  /** Company subdomain — used to link the brand's live certificate address. */
  subdomain?: string | null
  /** 'email' (header) or 'certificate' (page). Defaults to email. */
  surface?: LogoSurface
}

/** Brand's live certificate URL from its subdomain (env-aware), or null if unset. */
const certificateUrl = (subdomain?: string | null): string | null => {
  if (!subdomain) return null
  const host = window.location.hostname
  const isDev =
    host.includes('.dev.vera.inc') ||
    host.includes('localhost') ||
    import.meta.env.VITE_ENVIRONMENT === 'development'
  return `https://${subdomain}${isDev ? '.app.dev.vera.inc' : '.app.vera.inc'}`
}

const ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
]

const SIZE_OPTIONS = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
]

/**
 * Sun/moon, matching `EmailPreview`'s appearance toggle exactly — same glyphs, same size,
 * same icon-only shape. The two controls answer the same question ("how does this look
 * when a background flips") about two different objects, so they should not look like two
 * different kinds of control.
 */
const APPEARANCE_OPTIONS = [
  { label: '', ariaLabel: 'On light', value: 'light', icon: <SunIcon size={15} strokeWidth={1.5} /> },
  { label: '', ariaLabel: 'On dark', value: 'dark', icon: <MoonIcon size={15} strokeWidth={1.5} /> },
]

/**
 * The surface a dark-variant logo is actually drawn on.
 *
 * This is `darkEmailTokens.containerBackground` from
 * `packages/emails/src/components/base-email.tsx` — the email card's dark fill, i.e. the
 * exact colour a `logo_dark` asset sits against in a real inbox. Copied rather than
 * imported because `apps/platform` does not depend on `@repo/emails` (checked), and taking
 * that dependency to carry one hex would pull react-email's whole tree into the platform
 * bundle. If the email's dark card colour ever changes, this needs to change with it.
 */
const DARK_LOGO_BACKDROP = '#1C1C1F'

/**
 * Ink drawn ON the tile backdrop.
 *
 * The backdrop is a fixed white or a fixed near-black (see the long note in the tile), so
 * it does NOT follow the app's theme — and therefore nothing painted on top of it may read
 * from `theme.palette` either. A `text.secondary` on the white tile is invisible the
 * moment the platform is switched to dark mode.
 *
 * The dark trio is `darkEmailTokens`' own `borderColor` / `mutedColor` / `textColor`
 * verbatim, so the empty state's label sits at exactly the muted weight a real dark email
 * would give it. The light trio is their mirror.
 */
const LIGHT_ON_BACKDROP = {
  border: 'rgba(0,0,0,0.10)',
  // `muted` (0.55) is the weight a passive caption wants. The empty state is not passive —
  // it is the card's call to action — so it gets `action`, measured at 6.4:1 on white
  // against muted's 4.7:1. Both clear WCAG AA; the CTA should not be the faintest thing
  // in the card, which is what 0.55 made it once the tile's opacity was also in play.
  action: 'rgba(0,0,0,0.70)',
  strong: 'rgba(0,0,0,0.90)',
}
const DARK_ON_BACKDROP = {
  border: 'rgba(255,255,255,0.10)',
  action: 'rgba(255,255,255,0.72)',
  strong: 'rgba(255,255,255,0.92)',
}

interface LogoTileProps {
  label: string
  /** Artwork for the appearance currently being shown. Absent -> the empty state. */
  src?: string | null
  /**
   * Whether this VARIANT exists at all, which is a different question from whether the
   * currently-shown appearance has artwork. Selection is per-variant and applies to both
   * appearances, so a brand that uploaded a wordmark but no DARK wordmark still has a
   * selectable, selected wordmark tile — it just shows the empty state while you are
   * looking at dark. Keying the selected border on `src` instead would make a chosen
   * variant appear to de-select the moment you flipped the toggle.
   */
  selectable: boolean
  isSelected: boolean
  /** Dark backdrop behind the artwork, for a logo drawn to sit on a dark surface. */
  isDark: boolean
  onSelect: () => void
  onManage: () => void
}

/**
 * Hoisted OUT of `LogoUploadCard` on purpose. It used to be declared inside the component
 * body, which makes a new component type on every render and therefore remounts the tile
 * rather than updating it — harmless while the card had no state, but it discards the
 * 120ms border/background transition below, and adding the appearance toggle gives this
 * card state that changes often enough to see it.
 */
const LogoTile = ({
  label,
  src,
  selectable,
  isSelected,
  isDark,
  onSelect,
  onManage,
}: LogoTileProps) => {
  const chosen = isSelected && selectable
  const hasArtwork = Boolean(src)
  return (
    <Box
      // Only a real control when there is something to choose. An empty tile holds a link
      // to the Company page, and a link nested inside `role="button"` is both invalid and
      // unreachable for a screen-reader user driving by role.
      role={selectable ? 'button' : undefined}
      aria-pressed={selectable ? chosen : undefined}
      onClick={() => selectable && onSelect()}
      /*
        The canonical SELECTABLE-CARD treatment (§34), which this tile did not follow.
        Canonical implementation: `FeaturesPage`.

        Two things were wrong, and the second is the one that actually broke the
        control. The selected border was `primary.main` — an INK token, literally
        #000000 in light mode — which is why a "selected" logo read as a plain black
        outline rather than as chosen. And HOVER set a border too, in the same colour
        as selected, so moving the pointer across the row made every tile look
        selected in turn and the real selection became unfindable.

        §34's rule is the fix: TINT means "considering", BORDER means "chosen". Hover
        therefore changes the fill and never the border on a selectable card. A
        selected tile keeps its full tint on hover so it does not appear to
        de-select when the pointer lands on it.

        Closes VRG-77-Platform.Studio.Selectable.Card.Hover, which named this exact
        component.
      */
      sx={(theme) => ({
        flex: 1,
        p: 2,
        borderRadius: 2,
        cursor: selectable ? 'pointer' : 'default',
        border: `1.5px solid ${
          chosen ? theme.palette.brand[500] : theme.palette.primary[100]
        }`,
        backgroundColor: chosen ? alpha(theme.palette.brand[500], 0.1) : 'transparent',
        /*
          NO BLANKET `opacity` HERE. A non-selectable tile used to render at `opacity: 0.7`,
          which multiplies through to EVERY descendant — so the white swatch below came out
          at roughly #B5B5B5 over the dark page and its "Not added" label faded with it,
          to the point of being hard to read. The one tile whose whole job is to say
          "nothing here yet, go add one" was the least legible thing in the card.

          Non-selectability is already carried by the cursor, by the absence of a selected
          border, and now by the explicit "Not added" link — none of which cost contrast.
          Dimming a container is never the right way to say "inactive" when the container
          holds the call to action.
        */
        transition: 'border-color 120ms linear, background-color 120ms linear',
        '&:hover': selectable
          ? {
              backgroundColor: alpha(theme.palette.brand[500], chosen ? 0.1 : 0.06),
            }
          : {},
      })}
    >
      <Box
        sx={{
          height: 64,
          borderRadius: 1,
          /*
            THE BACKDROP IS THE SURFACE BEING PREVIEWED, NOT A STATE INDICATOR. A tile
            answers "how does this logo sit on white / on dark", so the swatch is white for
            the light variants and the email's own dark card fill for the dark ones —
            whether or not artwork has been uploaded yet. An empty tile is an empty white
            surface awaiting a logo, which is the truthful thing to show.

            This REVERSES an earlier decision here, on Spencer's call (2026-09-05). Empty
            tiles used to fall back to `fieldFill`, reasoning that white "read as a bright
            slab drawing the eye to the one slot with nothing in it". The counter-argument
            that won: the empty slot is exactly where the eye SHOULD go — it is the thing
            that needs action — and making three tiles white and one grey said "disabled"
            rather than "empty", while also breaking the row's rhythm.

            Consequence, and it is the reason these are literals and not palette tokens:
            the backdrop is now FIXED regardless of the app's own light/dark theme. Every
            colour drawn ON it must therefore key off the BACKDROP, not off `theme`. A
            `text.secondary` here would be near-white on white the moment the platform is
            in dark mode — the exact class of resolves-to-invisible bug this file has
            already been bitten by twice.
          */
          backgroundColor: isDark ? DARK_LOGO_BACKDROP : '#fff',
          border: `1px solid ${isDark ? DARK_ON_BACKDROP.border : LIGHT_ON_BACKDROP.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'background-color 120ms linear, border-color 120ms linear',
          mb: 1.5,
        }}
      >
        {hasArtwork ? (
          <img
            src={src as string}
            alt={label}
            style={{ maxHeight: '78%', maxWidth: '85%', objectFit: 'contain' }}
          />
        ) : (
          /*
            The empty state is the ONLY affordance for the thing it is reporting missing,
            so it is a link rather than a label. The card header's "Manage logos" goes to
            the same place, but this is where the gap is actually noticed — and the dark
            pair in particular is unset for most brands, so flipping the toggle is the
            moment a brand discovers it has two more assets to upload.

            `stopPropagation` because the tile itself is clickable whenever the variant is
            selectable: without it, choosing "go add one" would also silently change which
            variant this template uses.

            `ArrowUpRight` deliberately matches the header button (§21 — same icon for the
            same action) and reads as navigation rather than deletion (§38).
          */
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onManage()
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              border: 0,
              padding: 0,
              background: 'none',
              cursor: 'pointer',
              // Keyed to the BACKDROP, never to `theme` — see the tile's note.
              color: (isDark ? DARK_ON_BACKDROP : LIGHT_ON_BACKDROP).action,
              transition: 'color 120ms linear',
              '&:hover': {
                color: (isDark ? DARK_ON_BACKDROP : LIGHT_ON_BACKDROP).strong,
              },
            }}
          >
            <Typography variant="body4" color="inherit">
              Not added
            </Typography>
            <ArrowUpRight size={13} />
          </Link>
        )}
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body3" sx={{ fontWeight: chosen ? 600 : 400 }}>
          {label}
        </Typography>
        {chosen && <Check size={15} />}
      </Stack>
    </Box>
  )
}

/**
 * Logo card (VERA-270 / VERA-267). A brand *selects* which existing logo asset —
 * Mark (square) or Wordmark (wide) — this surface uses, rather than uploading
 * here; uploads live on the Company page (one source of truth). The choice is
 * per-surface (`email_logo_variant` / `certificate_logo_variant`) so a brand can
 * use the wordmark in emails and the mark on certificates, etc., plus the
 * placement controls (alignment + size). All persist with the company styles.
 *
 * THERE ARE FOUR LOGO ASSETS, NOT TWO. `companies` carries `logo` / `logo_dark` /
 * `logo_wide` / `logo_wide_dark` (VRG-18), the Company page uploads all four, and the
 * email already swaps light for dark at render time. Studio, however, was passed only the
 * two LIGHT ones and had no way to show the others — so a brand could upload a dark
 * wordmark and never see it again. The appearance toggle is that missing surface; it is
 * inspection only, and changes nothing that is saved.
 *
 * ## What each surface does with the selection (VRG-560 — read before editing)
 *
 * - **Email — LIVE.** `email_logo_variant` is honoured by the Studio preview, by
 *   Labs, and by REAL SENDS. All three resolve the light/dark pair through the
 *   one shared resolver (`apps/api/src/libs/emails/company-brand-tokens.ts`), so
 *   what this control selects is what a recipient receives.
 *
 *   Until 2026-09-05 nothing read the setting at all — every path hardcoded
 *   `companies.logo` — so picking "Wordmark" changed the tile's border and
 *   nothing else, in the preview OR in the brand's actual email. If you are
 *   changing this control, the failure to avoid reintroducing is a selector whose
 *   value never reaches a render.
 *
 * - **Certificate — NOT live.** `certificate_logo_variant` is stored and is still
 *   consumed by nothing. Deferred to App.Certificates (see the `TODO(VERA-315 /
 *   TD-067)` below). So the two toggles on this card do NOT have equal standing
 *   today, which is worth knowing before treating one as evidence for the other.
 *
 * A variant tile with no asset behind it is unclickable (`isEmpty`), which is the
 * control's own guard against selecting a variant the brand cannot render. The
 * server ALSO falls back (wordmark -> mark, as a pair) because the styles can
 * outlive the asset: uploads happen on the Company page, so a wordmark can be
 * removed there long after "Wordmark" was saved here.
 *
 * ⚠️ A paragraph here used to read "the wordmark isn't stored in the backend yet
 * (VRG-18) … applying the chosen variant in the email/certificate render is
 * deferred too". It is deleted rather than softened, on two independent counts:
 * `logo_wide` / `logo_wide_dark` ARE stored and ARE resolved
 * (`resolveEmailLogoPair`, `apps/api/src/libs/emails/company-brand-tokens.ts`),
 * and the paragraph directly above it already said all four are carried and
 * uploaded. It described the state before 2026-09-05 and was contradicted by its
 * own docblock. The half that remains true — certificate consumption is deferred
 * — is stated in the Certificate bullet above, with the live TODO anchor.
 */
export const LogoUploadCard = ({
  markLogo,
  markLogoDark,
  wordmarkLogo,
  wordmarkLogoDark,
  subdomain,
  surface = 'email',
}: LogoUploadCardProps) => {
  const isCertificate = surface === 'certificate'
  const certUrl = certificateUrl(subdomain)
  const certHost = certUrl?.replace(/^https?:\/\//, '')
  const alignHelp = isCertificate
    ? 'Where your logo sits on the certificate.'
    : 'Where your logo sits in the email header.'
  const sizeHelp = isCertificate
    ? 'How large the logo appears on the certificate.'
    : 'How large the logo appears in the email header.'

  const navigate = useNavigate()
  // Carries "← Back to Studio" to the Company page.
  const referrer = useReferrerState('Studio')
  const goToManageLogos = () =>
    navigate(ROUTES.SETTINGS_COMPANY_GENERAL, { state: referrer })

  const pageForm = useFormContext()
  const variantField = isCertificate ? 'certificate_logo_variant' : 'email_logo_variant'
  const selectedVariant = ((pageForm?.watch(variantField) as LogoVariant) || 'mark')
  const selectVariant = (v: LogoVariant) =>
    pageForm?.setValue(variantField, v, { shouldDirty: true })

  // Local, unsaved, and per-card — see `LogoAppearance`. It resets to light on remount,
  // which is correct: light is the required variant and the one every brand has.
  const [appearance, setAppearance] = useState<LogoAppearance>('light')
  const isDark = appearance === 'dark'

  const align = (pageForm?.watch('logo_align') as string) || 'left'
  const size = (pageForm?.watch('logo_size') as string) || 'medium'

  return (
    // `@repo/ui`'s `Card` forwards only a named prop list, not arbitrary DOM
    // attributes, so the product-tour anchor (working-conventions §42) goes on
    // this wrapping `Box` instead of on the `Card` itself.
    <Box data-tour="studio-logo-upload">
    <Card
      title="Logo"
      spacing={6}
      action={
        <Button
          variant="tertiary"
          endIcon={<ArrowUpRight size={15} />}
          onClick={goToManageLogos}
        >
          Manage logos
        </Button>
      }
    >
      <Stack>
        {/*
          The toggle sits on the heading row rather than under the tiles because it governs
          what the tiles SHOW — putting it below would read as a property of the selection
          the user had just made, which is exactly what it is not.
        */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
          pb={2}
        >
          <Typography variant="body2" color="text.primary">
            Brand logo
          </Typography>
          <ToggleButton
            tabs={APPEARANCE_OPTIONS}
            tab={appearance}
            onChange={(v) => setAppearance(v as LogoAppearance)}
            sx={{ flexShrink: 0 }}
          />
        </Stack>
        <Typography variant="body3" color="text.secondary" pb={4}>
          Choose which logo this {isCertificate ? 'certificate' : 'email'} uses.{' '}
          {isDark
            ? 'These are the versions shown when the reader is in dark mode.'
            : 'These are the versions shown on a light background.'}
        </Typography>
        <Stack direction="row" spacing={3}>
          <LogoTile
            label="Mark"
            src={isDark ? markLogoDark : markLogo}
            selectable={Boolean(markLogo)}
            isSelected={selectedVariant === 'mark'}
            isDark={isDark}
            onSelect={() => selectVariant('mark')}
            onManage={goToManageLogos}
          />
          <LogoTile
            label="Wordmark"
            src={isDark ? wordmarkLogoDark : wordmarkLogo}
            selectable={Boolean(wordmarkLogo)}
            isSelected={selectedVariant === 'wordmark'}
            isDark={isDark}
            onSelect={() => selectVariant('wordmark')}
            onManage={goToManageLogos}
          />
        </Stack>
      </Stack>

      {/*
        Certificate-only note, GROWN AND SHRUNK rather than swapped in and out.

        This card is the one thing both Studio tabs share, and this paragraph is the only
        difference between them — so switching tabs used to jump the card 480px <-> 525px in
        a single frame. Spencer read that as the card "flashing", and he was reading it
        correctly: nothing signalled that the two states were the same card with one more
        sentence, so it looked like a different page swapping in.

        A TRANSITION, not the keyframes used by `TokenImpactPanel`, and that choice depends on
        a measured fact: React keeps this card's DOM node ACROSS the tab switch (verified —
        the node compares identical before and after), so there is a persistent element for a
        transition to run on, and it therefore animates in BOTH directions. Keyframes would
        only fire on mount, which is exactly the case that does not happen here.

        `grid-template-rows: 1fr -> 0fr` for the same reason as the impact panel: the height
        is unknown, and `height: auto` does not interpolate.

        The negative margin is not a hack for its own sake. The `Card` lays its children out
        with `spacing={6}`, so a child collapsed to zero height still leaves its 24px gap
        behind — measured: the card went 640 -> 618, shrinking by the 22px paragraph while
        keeping a 24px dead band where it had been. Pulling exactly one gap back as the row
        closes removes it, and transitioning the two together keeps them in step instead of
        closing the gap after the text has already gone.

        It needs `&&&`. `Card` renders its children inside a MUI `Stack`, whose spacing is a
        `> :not(style) ~ :not(style) { margin-top }` rule — a sibling selector that
        out-specifies a plain `sx` margin, so the first attempt at this computed to 24px in
        BOTH states and did nothing. Tripling the class wins outright rather than relying on
        Emotion's insertion order, which a mere tie would.

        `aria-hidden` while collapsed: the text stays in the DOM so it can be animated, and a
        screen reader must not read a certificate note to someone on the Email tab.

        TODO(VERA-315 / TD-067): the actual show/hide-logo logic (show at the subdomain, hide
        when Vera is embedded in the brand's own site) plus applying
        logo_align/logo_size/variant and hide-Powered-by-Vera to the certificate render lives
        in the App.Certificates main page epic. Studio only authors the controls + this note.
      */}
      <Box
        aria-hidden={!isCertificate}
        sx={{
          display: 'grid',
          gridTemplateRows: isCertificate ? '1fr' : '0fr',
          '&&&': { marginTop: isCertificate ? undefined : '0px' },
          transition:
            'grid-template-rows 260ms cubic-bezier(0.32, 0.72, 0, 1), margin-top 260ms cubic-bezier(0.32, 0.72, 0, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {certUrl ? (
              <>
                Certificates at{' '}
                <Link href={certUrl} target="_blank" rel="noopener">
                  {certHost}
                </Link>{' '}
                always show this logo.
              </>
            ) : (
              'Certificates at your Vera address always show this logo.'
            )}{' '}
            When Vera is embedded in your own website, it&apos;s hidden so your site&apos;s header can show instead.
          </Typography>
        </Box>
      </Box>

      <Divider />
      <Stack>
        <Typography variant="body2" color="text.primary" pb={2}>
          Logo alignment
        </Typography>
        <Typography variant="body3" color="text.secondary" pb={4}>
          {alignHelp}
        </Typography>
        <ToggleButton
          tabs={ALIGN_OPTIONS}
          tab={align}
          onChange={(v) => pageForm?.setValue('logo_align', v, { shouldDirty: true })}
        />
      </Stack>
      <Divider />
      <Stack>
        <Typography variant="body2" color="text.primary" pb={2}>
          Logo size
        </Typography>
        <Typography variant="body3" color="text.secondary" pb={4}>
          {sizeHelp}
        </Typography>
        <ToggleButton
          tabs={SIZE_OPTIONS}
          tab={size}
          onChange={(v) => pageForm?.setValue('logo_size', v, { shouldDirty: true })}
        />
      </Stack>
    </Card>
    </Box>
  )
}
